import merge from 'lodash/merge';
import { useMemo, useEffect, ReactNode } from 'react';

// @mui
import { CssBaseline } from '@mui/material';
import {
  extendTheme,
  useColorScheme,
  CssVarsProvider,
  StyledEngineProvider,
} from '@mui/material/styles';

//
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
// hooks
import useSettings from '../hooks/useSettings';
import shadows, { customShadows } from './shadows';

declare module '@mui/material/styles' {
  interface Theme {
    vars: {
      palette: {
        primary: {
          lighter: string;
          light: string;
          main: string;
          dark: string;
          darker: string;
        };
        secondary: {
          lighter: string;
          light: string;
          main: string;
          dark: string;
          darker: string;
        };
        text: {
          primary: string;
          secondary: string;
          disabled: string;
          contrast: string;
        };
        grey: {
          [key: string]: string;
          '500Channel': string;
        };
        error: {
          mainChannel: string;
        };
      };
    };
  }
}

// ----------------------------------------------------------------------

type Props = {
  children: ReactNode;
};

function ThemeModeSync() {
  const { themeMode } = useSettings();
  const { setMode } = useColorScheme();

  useEffect(() => {
    if (setMode && themeMode) {
      setMode(themeMode);
    }
  }, [setMode, themeMode]);

  return null;
}

function ShadowSync() {
  const { mode } = useColorScheme();

  useEffect(() => {
    if (mode) {
      const root = document.documentElement;
      if (mode === 'dark') {
        root.classList.add('dark');
        root.classList.remove('light');
      } else {
        root.classList.add('light');
        root.classList.remove('dark');
      }
    }
  }, [mode]);

  return null;
}

export default function ThemeProvider({ children }: Props) {
  const { themeMode, themeDirection } = useSettings();

  const theme = useMemo(() => {
    const baseTheme = extendTheme({
      colorSchemes: {
        light: {
          palette: palette.light,
        },
        dark: {
          palette: palette.dark,
        },
      },
      colorSchemeSelector: 'class',
      typography,
      breakpoints,
      shape: { borderRadius: 8 },
      direction: themeDirection,
      shadows: shadows.light,
    });

    (baseTheme as any).customShadows = customShadows.light;

    baseTheme.components = merge(componentsOverride(baseTheme));
    return baseTheme;
  }, [themeDirection]);

  return (
    <StyledEngineProvider injectFirst>
      <CssVarsProvider theme={theme} defaultMode={themeMode} colorSchemeStorageKey="theme-mode">
        <ThemeModeSync />
        <ShadowSync />
        <CssBaseline />
        {children}
      </CssVarsProvider>
    </StyledEngineProvider>
  );
}
